import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { camelizeKeys } from 'humps';
import { purchase as purchaseApi } from 'js-api-client';
import useDebounce from 'hooks/useDebounce';

/**
 * useRucValidation Hook
 *
 * Custom hook to handle RUC validation logic and state management.
 * Focused only on validation operations with debounced input handling.
 *
 * @param {string} purchaseToken - Token used for purchase operations
 * @returns {object} Object containing RUC validation state and essential functions
 */
const useRucValidation = (purchaseToken) => {
  const { t } = useTranslation('exchange');

  const [rucNumber, setRucNumber] = useState('');
  const [isValidatingRuc, setIsValidatingRuc] = useState(false);
  const [taxpayerData, setTaxpayerData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const debouncedRucNumber = useDebounce(rucNumber, 500);

  /**
   * handleRucValidation Function
   *
   * Handles the RUC validation using the existing service.
   * Includes protection against validation spam using isValidatingRuc state.
   *
   * @param {string} rucValue - The RUC value to validate.
   * @returns {Promise<void>} A promise that resolves when validation is completed.
   */
  const handleRucValidation = useCallback(
    async (rucValue) => {
      if (!rucValue || rucValue.length < 11) {
        setTaxpayerData(null);
        setErrorMessage('');
        return;
      }

      setIsValidatingRuc(true);
      setErrorMessage('');

      try {
        const params = { taxpayerId: rucValue, purchaseToken };

        const taxPayerResponse = await purchaseApi.validateTaxPayer(params);
        const camelizedTaxPayerResponse = camelizeKeys(taxPayerResponse);

        if (!camelizedTaxPayerResponse || !camelizedTaxPayerResponse.body) {
          setTaxpayerData(null);
          setErrorMessage(t('modal_confirm_billing.invalid_ruc'));
          return;
        }

        const { taxpayerLegalName, taxpayerAddress } = camelizedTaxPayerResponse.body;

        if (!taxpayerLegalName || taxpayerLegalName === 'unknown or empty RUC parameter') {
          setTaxpayerData(null);
          setErrorMessage(t('modal_confirm_billing.invalid_ruc'));
        } else {
          setTaxpayerData({
            legalName: taxpayerLegalName,
            address: taxpayerAddress || '',
          });
          setErrorMessage('');
        }
      } catch (error) {
        setTaxpayerData(null);
        setErrorMessage(t('modal_confirm_billing.invalid_ruc'));
      } finally {
        setIsValidatingRuc(false);
      }
    },
    [purchaseToken, t],
  );

  /**
   * handleRucChange Function
   *
   * Handles the RUC input change without immediate validation.
   * Validation is now handled automatically by the debounced useEffect.
   *
   * @param {Event} event - The input change event.
   * @returns {void}
   */
  const handleRucChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');
    setRucNumber(value);

    setTaxpayerData(null);
    setErrorMessage('');
  };

  /**
   * resetState Function
   *
   * Resets all state values to initial state.
   *
   * @returns {void}
   */
  const resetState = () => {
    setRucNumber('');
    setTaxpayerData(null);
    setErrorMessage('');
    setIsValidatingRuc(false);
  };

  useEffect(() => {
    setRucNumber('');
    setTaxpayerData(null);
    setErrorMessage('');
    setIsValidatingRuc(false);
  }, [purchaseToken]);

  useEffect(() => {
    if (debouncedRucNumber && debouncedRucNumber.length === 11) {
      handleRucValidation(debouncedRucNumber);
    }
  }, [debouncedRucNumber, handleRucValidation]);

  return {
    rucNumber,
    isValidatingRuc,
    taxpayerData,
    errorMessage,
    handleRucValidation,
    handleRucChange,
    resetState,
    setErrorMessage,
  };
};

export default useRucValidation;
